import { Country } from '@eo-storefronts/eo-core'
import { autocompleteClasses, Box, FormControlLabel, FormGroup, inputBaseClasses } from '@mui/material'
import FormikSelectField from '~/src/components/mui-wrappers/fields/FormikSelectField'
import { useTranslations } from '~/src/hooks/useTranslations'
import { FormikProps } from 'formik'
import { LANGUAGE_STATE, SUPPORTED_COUNTRIES_SELECTOR } from '~/src/stores/lang'
import { AddressFormInterface } from '~/src/types/forms/AddressFormInterface'
import FormikTextField from '~/src/components/mui-wrappers/fields/FormikTextField'
import { useEoValue } from '~/src/hooks/useEoState'
import Toggle from '~/src/components/mui-wrappers/styled/toggle'
import FormikAddressAutocomplete from '~/src/components/mui-wrappers/fields/address'

interface Props {
  form: FormikProps<AddressFormInterface>,
  showSetAsDefault: boolean,
  showDescription: boolean,
}

const AddressFormFields = ({ form, showSetAsDefault, showDescription }: Props) => {
  const lang = useEoValue(LANGUAGE_STATE)
  const supportedCountries = useEoValue(SUPPORTED_COUNTRIES_SELECTOR)
  const { t } = useTranslations()

  const _getGeocoderAddressComponentElement = (
    address: google.maps.GeocoderResult,
    type: string
  ): google.maps.GeocoderAddressComponent | undefined => {
    return address.address_components.filter((component: google.maps.GeocoderAddressComponent) => {
      return component.types.includes(type)
    })[0]
  }

  const _handleOnAddressSelected = async (address: google.maps.GeocoderResult | null): Promise<void> => {
    if (!address) {
      return
    }

    const streetNumber: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'street_number')
    const street: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'route')
    const subLocality: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'sublocality')
    const locality: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'locality')
    const postalCode: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'postal_code')
    const country: google.maps.GeocoderAddressComponent | undefined = _getGeocoderAddressComponentElement(address, 'country')

    const setFieldPromises = [
      form.setFieldValue('street', `${street?.long_name} ${streetNumber?.long_name ?? ''}`),
      form.setFieldValue('zipCode', postalCode?.long_name),
      form.setFieldValue('locality', subLocality?.long_name || locality?.long_name)
    ]

    if (country?.short_name) {
      setFieldPromises.push(form.setFieldValue('country', country.short_name))
    }

    await Promise.all(setFieldPromises)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexDirection: 'column'
      }}
    >
      {showDescription && (
        <FormikTextField
          TextFieldProps={{
            form,
            name: 'name',
            color: 'container',
            label: t('profile.address.description'),
            helperText: t('global.required')
          }}
        />
      )}
      <FormikAddressAutocomplete
        TextFieldProps={{
          form,
          name: 'street',
          color: 'container',
          label: t('profile.address.streetAndHouseNumber'),
          helperText: t('global.required'),
          autoComplete: 'new-street',
          sx: {
            [`.${inputBaseClasses.root}`]: {
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 0,
              [`.${autocompleteClasses.input}`]: {
                paddingTop: 1.5,
                paddingBottom: 1.5,
                paddingLeft: 1.75,
                paddingRight: 1.75
              }
            }
          }
        }}
        onAddressSelected={_handleOnAddressSelected}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: 2
        }}
      >
        <FormikTextField
          TextFieldProps={{
            form,
            name: 'bus',
            color: 'container',
            label: t('profile.address.bus')
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: 2
        }}
      >
        <FormikTextField
          TextFieldProps={{
            form,
            name: 'zipCode',
            color: 'container',
            label: t('profile.address.zipCode'),
            helperText: t('global.required')
          }}
        />
        <FormikTextField
          TextFieldProps={{
            form,
            name: 'locality',
            color: 'container',
            label: t('profile.address.locality'),
            helperText: t('global.required')
          }}
        />
      </Box>

      {supportedCountries && (
        <FormikSelectField
          SelectFieldProps={{
            form: form,
            name: 'country',
            color: 'container',
            label: t('profile.address.country'),
            helperText: t('global.required'),
            defaultValue: undefined
          }}
        >
          <option value={undefined}></option>
          {supportedCountries
            .map((country: Country) => (
              <option
                key={country.id}
                value={country.iso}
              >
                {country.name[lang]}
              </option>
            ))}
        </FormikSelectField>
      )}

      {showSetAsDefault && (
        <FormGroup>
          <FormControlLabel
            control={(
              <Toggle
                name='isDefault'
                checked={form.values.isDefault}
                onChange={form.handleChange}
              />
            )}
            label={t('profile.address.makeDefault')}
          />
        </FormGroup>
      )}
    </Box>
  )
}

export default AddressFormFields
